'use client';

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import type { OrderSummary } from '../../../infrastructure/OMSClient';
import { OrdersTable } from './components/OrdersTable';
import { useOMS } from '../../../apps/hooks/oms/useOMS';
import { getDefaultOrderDateRange, getNumberOfMonths } from '../../../utils/dateUtils';

export default function OrdersPage() {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const oms = useOMS();
  const fetchingRef = useRef(false);

  // Initialize date range with default values (from 2 months ago to current month)
  const defaultDateRange = getDefaultOrderDateRange();

  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem('selectedStartDate') || defaultDateRange.startDate;
  });

  const [endDate, setEndDate] = useState(() => {
    return localStorage.getItem('selectedEndDate') || defaultDateRange.endDate;
  });

  // Main fetch function that calls the utility to fetch orders across multiple months
  const fetchOrders = async (currentStartDate = startDate, currentEndDate = endDate) => {
    if (!currentStartDate || !currentEndDate || fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);

      console.log('Fetching orders for date range:', currentStartDate, 'to', currentEndDate);

      // Get all months in the date range
      const months = getNumberOfMonths(currentStartDate, currentEndDate);
      console.log('Months to fetch:', months);

      // Fetch all orders using the utility function
      const allOrders = await oms.listOrdersByDateRange(currentStartDate, currentEndDate);

      setOrders(allOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        fetchingRef.current = false;
      }, 1000);
    }
  };

  // Fetch orders when date range changes
  useEffect(() => {
    console.log('Date range changed:', startDate, 'to', endDate);
    fetchOrders(startDate, endDate);
  }, [startDate, endDate]);

  // Auto-refresh orders every 5 minutes
  useEffect(() => {
    const interval = setInterval(
      () => {
        console.log('Auto-refreshing orders for range:', startDate, 'to', endDate);
        fetchOrders(startDate, endDate);
      },
      5 * 60 * 1000,
    ); // 5 minutes

    return () => clearInterval(interval);
  });

  // Handle date range changes from the UI
  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    if (newStartDate !== startDate || newEndDate !== endDate) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      localStorage.setItem('selectedStartDate', newStartDate);
      localStorage.setItem('selectedEndDate', newEndDate);
    }
  };

  return (
    <div className="w-full max-w-[80%] mx-auto py-4 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold pb-3">Order Management Dashboard</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <OrdersTable
            orders={orders}
            isLoading={loading}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
